/**
 * SoftPlan/Construx submesh feature classification.
 * Blueprint: docs/MODEL_FEATURE_MAP.md
 */

/** Mirror of modelLocalize.layerKeyFromName — kept local for a self-contained classifier. */
function layerKeyFromName(name = '') {
  const n = String(name).toLowerCase();
  if (n.startsWith('layer_roof') || n === 'roof' || n.startsWith('roof_')) {
    return 'roof';
  }
  if (
    n.startsWith('layer_interior') ||
    n === 'ceiling' ||
    n.startsWith('ceiling_')
  ) {
    return 'interior';
  }
  return 'walls';
}

export const FEATURE_KINDS = [
  'roof',
  'ceiling',
  'slab',
  'door',
  'window',
  'appliances',
  'structure',
];

export const DEFAULT_FEATURE_VISIBILITY = {
  roof: true,
  ceiling: true,
  slab: true,
  door: true,
  window: true,
  appliances: true,
  structure: true,
};

function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}

function volumeOf(size) {
  const [a, b, c] = size || [0, 0, 0];
  return Math.abs(a) * Math.abs(b) * Math.abs(c);
}

function aabbOverlap(a, b, pad = 0) {
  if (!a?.min || !a?.max || !b?.min || !b?.max) return false;
  return (
    a.min[0] <= b.max[0] + pad &&
    a.max[0] >= b.min[0] - pad &&
    a.min[1] <= b.max[1] + pad &&
    a.max[1] >= b.min[1] - pad &&
    a.min[2] <= b.max[2] + pad &&
    a.max[2] >= b.min[2] - pad
  );
}

function dist3(a, b) {
  if (!a || !b) return Infinity;
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

/**
 * Classify a single mesh from world-space size (meters) + SoftPlan name.
 * @param {{ name?: string, size: [number, number, number], color?: string, id?: string }} mesh
 */
export function classifyMesh(mesh) {
  const name = String(mesh.name || '');
  const n = name.toLowerCase();
  const [sx, sy, sz] = mesh.size || [0, 0, 0];
  const dims = [sx, sy, sz].map((v) => Math.abs(Number(v) || 0));
  const sorted = [...dims].sort((a, b) => a - b);
  const thin = sorted[0];
  const mid = sorted[1];
  const tall = sorted[2];
  const height = sy; // SoftPlan Y-up after inch→m scale in lab
  const layerKey = layerKeyFromName(name);

  if (
    n.startsWith('layer_roof') ||
    n === 'roof' ||
    n.startsWith('roof_')
  ) {
    return {
      kind: 'roof',
      confidence: 0.95,
      layerKey: 'roof',
    };
  }
  if (
    n.startsWith('layer_interior') ||
    n === 'ceiling' ||
    n.startsWith('ceiling_')
  ) {
    return {
      kind: 'ceiling',
      confidence: 0.95,
      layerKey: 'interior',
    };
  }

  // Flat floor boards / slabs
  if (height < 0.15 && mid > 0.4 && tall > 0.4) {
    return {
      kind: 'slab',
      confidence: clamp(0.55 + (0.15 - height) * 2, 0.5, 0.85),
      layerKey: 'walls',
    };
  }

  // Door slab: thin, door-height, door-width
  // Slightly looser thin threshold so SoftPlan frame leaves (~0.13 m) can still qualify as leaf.
  if (
    thin < 0.14 &&
    height >= 1.9 &&
    height <= 2.55 &&
    mid >= 0.55 &&
    mid <= 1.25
  ) {
    const heightScore = 1 - Math.abs(height - 2.15) / 0.6;
    const widthScore = 1 - Math.abs(mid - 0.9) / 0.5;
    const conf = clamp(0.45 + 0.35 * heightScore + 0.2 * widthScore, 0.45, 0.92);
    return { kind: 'door', confidence: conf, layerKey: 'walls' };
  }

  // Window-ish thin panels
  if (
    thin < 0.12 &&
    height >= 0.55 &&
    height <= 1.85 &&
    mid >= 0.45 &&
    mid <= 2.6
  ) {
    const conf = clamp(0.4 + (1.85 - height) * 0.15, 0.4, 0.8);
    return { kind: 'window', confidence: conf, layerKey: 'walls' };
  }

  // Appliances — boxy fixtures (fridge / range / hood); not long cabinet runs
  const appliance = classifyAppliance(dims, height, mesh.center);
  if (appliance) return appliance;

  return {
    kind: 'structure',
    confidence: 0.5,
    layerKey: layerKey || 'walls',
  };
}

/**
 * Boxy kitchen/bath fixtures vs walls/cabinet runs — docs/MODEL_FEATURE_MAP.md § Appliances
 * @param {number[]} dims [sx, sy, sz] absolute meters
 * @param {number} height sy
 * @param {number[] | null | undefined} center
 */
function classifyAppliance(dims, height, center) {
  const [sx, , sz] = dims;
  const sorted = [...dims].sort((a, b) => a - b);
  const thin = sorted[0];
  const mid = sorted[1];
  const vol = volumeOf(dims);
  const maxH = Math.max(sx, sz);
  const minH = Math.min(sx, sz);
  const cy = Array.isArray(center) ? Number(center[1]) : NaN;

  // Tall box: refrigerator / freezer
  if (
    height >= 1.45 &&
    height <= 2.15 &&
    thin >= 0.45 &&
    mid <= 1.05 &&
    maxH <= 1.05 &&
    minH >= 0.45 &&
    vol >= 0.35 &&
    vol <= 1.55
  ) {
    const conf = clamp(0.55 + (1 - Math.abs(height - 1.8) / 0.5) * 0.25, 0.5, 0.9);
    return { kind: 'appliances', confidence: conf, layerKey: 'walls' };
  }

  // Counter-height box: range, dishwasher, compact units (not long runs)
  if (
    height >= 0.65 &&
    height <= 1.25 &&
    thin >= 0.38 &&
    mid <= 1.0 &&
    maxH <= 1.15 &&
    minH >= 0.35 &&
    vol >= 0.12 &&
    vol <= 0.85 &&
    thin / Math.max(mid, 1e-6) >= 0.45
  ) {
    const conf = clamp(0.5 + vol * 0.2, 0.45, 0.85);
    return { kind: 'appliances', confidence: conf, layerKey: 'walls' };
  }

  // Elevated hood / microwave band
  if (
    Number.isFinite(cy) &&
    cy >= 1.35 &&
    height >= 0.18 &&
    height <= 0.75 &&
    thin >= 0.22 &&
    mid <= 1.15 &&
    maxH <= 1.2 &&
    vol >= 0.03 &&
    vol <= 0.45
  ) {
    return { kind: 'appliances', confidence: 0.55, layerKey: 'walls' };
  }

  return null;
}

/**
 * Attach panel + handle companions to door leaves.
 * Mutates feature objects; returns { attached, features }.
 */
export function attachDoorSubitems(features, meshById) {
  const doors = features.filter((f) => f.kind === 'door' && !f.parentId);
  const claimed = new Set();
  let attached = 0;

  for (const door of doors) {
    const doorMesh = meshById.get(door.id);
    if (!doorMesh) continue;
    const doorH = Math.abs(door.size?.[1] || 0);
    const doorThin = Math.min(
      ...(door.size || [0, 0, 0]).map((v) => Math.abs(v))
    );
    const subIds = [];

    for (const feat of features) {
      if (feat.id === door.id) continue;
      if (feat.kind === 'roof' || feat.kind === 'ceiling') continue;
      if (feat.kind === 'appliances') continue;
      if (feat.parentId) continue;
      if (claimed.has(feat.id)) continue;

      const mesh = meshById.get(feat.id);
      if (!mesh?.min || !mesh?.max) continue;
      if (!aabbOverlap(doorMesh, mesh, 0.2)) continue;

      const size = mesh.size || feat.size || [0, 0, 0];
      const dims = size.map((v) => Math.abs(Number(v) || 0));
      const sorted = [...dims].sort((a, b) => a - b);
      const thin = sorted[0];
      const mid = sorted[1];
      const tall = sorted[2];
      const height = dims[1];
      const vol = volumeOf(dims);
      const centerDist = dist3(doorMesh.center, mesh.center);

      // Handles / hardware — small pulls, knobs, latches
      const isHandle =
        centerDist < 0.85 &&
        (tall < 0.35 || vol < 0.015) &&
        tall < 0.5 &&
        vol < 0.05;

      // Companion panels / thicker leaves still in the opening
      const heightClose = Math.abs(height - doorH) < 0.45;
      const doorMid = [...(door.size || [0, 0, 0])]
        .map((v) => Math.abs(v))
        .sort((a, b) => a - b)[1];
      const widthClose = Math.abs(mid - doorMid) < 0.45;
      const isPanel =
        !isHandle &&
        heightClose &&
        widthClose &&
        thin < 0.22 &&
        mid >= 0.4 &&
        mid <= 1.35 &&
        height >= 1.9 &&
        height <= 2.65 &&
        vol < 0.7 &&
        centerDist < 0.35 &&
        // Prefer thicker-than-leaf companions, or same-family thin leftovers
        (thin >= doorThin * 0.8 || feat.kind === 'structure');

      // Avoid claiming huge wall shells / cabinets that graze the door AABB
      if (vol > 1.5 || mid > 1.6 || tall > 3.2) continue;

      let role = null;
      if (isHandle) role = 'handle';
      else if (isPanel) role = 'panel';
      else if (
        feat.kind === 'door' &&
        centerDist < 0.25 &&
        heightClose
      ) {
        // Nested door leaves in the same opening — keep as sibling leaf under nearest primary
        role = 'leaf';
      } else {
        continue;
      }

      // Don't demote a stronger primary door into a weak parent's subitem
      if (
        feat.kind === 'door' &&
        !feat.parentId &&
        (feat.confidence || 0) > (door.confidence || 0) + 0.05
      ) {
        continue;
      }

      feat.kind = 'door';
      feat.role = role;
      feat.parentId = door.id;
      feat.layerKey = 'walls';
      feat.confidence = clamp(
        (feat.confidence || 0.5) * 0.85,
        0.35,
        0.88
      );
      claimed.add(feat.id);
      subIds.push(feat.id);
      attached += 1;
    }

    door.role = door.role || 'leaf';
    door.subIds = subIds;
  }

  return { attached, features };
}

/**
 * @param {Array<{
 *   id?: string, uuid?: string, name?: string,
 *   size: [number,number,number],
 *   center?: [number,number,number],
 *   min?: [number,number,number],
 *   max?: [number,number,number],
 *   color?: string
 * }>} meshList
 */
export function classifyMeshes(meshList = []) {
  const features = [];
  const meshById = new Map();

  meshList.forEach((mesh, i) => {
    const id = String(mesh.id || mesh.uuid || `m-${i}`);
    const classified = classifyMesh(mesh);
    const record = {
      id,
      name: mesh.name || '',
      kind: classified.kind,
      role: classified.kind === 'door' ? 'leaf' : undefined,
      parentId: null,
      subIds: classified.kind === 'door' ? [] : undefined,
      size: mesh.size,
      center: mesh.center || null,
      min: mesh.min || null,
      max: mesh.max || null,
      color: mesh.color || null,
      confidence: classified.confidence,
      layerKey: classified.layerKey,
    };
    features.push(record);
    meshById.set(id, {
      ...mesh,
      id,
      size: mesh.size,
      center: mesh.center,
      min: mesh.min,
      max: mesh.max,
    });
  });

  attachDoorSubitems(features, meshById);

  const counts = {
    roof: 0,
    ceiling: 0,
    slab: 0,
    door: 0,
    window: 0,
    appliances: 0,
    structure: 0,
  };
  features.forEach((f) => {
    counts[f.kind] = (counts[f.kind] || 0) + 1;
  });

  const doorLeaves = features.filter(
    (f) => f.kind === 'door' && !f.parentId
  ).length;
  const doorSubitems = features.filter(
    (f) => f.kind === 'door' && f.parentId
  ).length;

  return {
    version: 'v0.2',
    features,
    counts,
    doorLeaves,
    doorSubitems,
  };
}

/** Map feature kind → coarse layer toggle (roof / walls / interior). */
export function featureKindToLayer(kind) {
  if (kind === 'roof') return 'roof';
  if (kind === 'ceiling') return 'interior';
  return 'walls';
}

/** True when this feature should hide/highlight with the door family. */
export function isDoorFamily(feature) {
  return feature?.kind === 'door';
}
